/**
 * UI Store - Zustand store for UI state management.
 *
 * Manages the current view, sidebar state, notifications, selected date,
 * and module navigation state for the Kira Productivity Suite App Shell.
 * Keeps UI concerns separated from data and sync logic.
 */

import { create } from 'zustand';
import type { ModuleId } from '../types/modules';
import { db } from '../services/localDb';

// ─── Types ───────────────────────────────────────────────────────────────────

export type ViewName = 'calendar' | 'editor' | 'settings';

export interface Notification {
  /** Unique notification ID. */
  id: string;
  /** Notification message. */
  message: string;
  /** Notification type for styling. */
  type: 'info' | 'success' | 'warning' | 'error';
  /** Timestamp when the notification was created. */
  createdAt: number;
}

export interface UIState {
  /** The currently active view/screen. */
  currentView: ViewName;
  /** Whether the sidebar is open. */
  sidebarOpen: boolean;
  /** Active notifications to display. */
  notifications: Notification[];
  /** The currently selected date in the calendar (YYYY-MM-DD or null). */
  selectedDate: string | null;
  /** The currently active module in the App Shell navigation. */
  activeModule: ModuleId;
  /** The last active module from the previous session (persisted for restore on launch). */
  lastActiveModule: ModuleId;
}

export interface UIActions {
  /** Navigate to a specific view. */
  setView: (view: ViewName) => void;
  /** Toggle the sidebar open/closed. */
  toggleSidebar: () => void;
  /** Add a new notification. */
  addNotification: (message: string, type?: Notification['type']) => void;
  /** Dismiss (remove) a notification by ID. */
  dismissNotification: (id: string) => void;
  /** Set the selected date in the calendar. */
  setSelectedDate: (date: string | null) => void;
  /** Switch the active module, persisting the previous module as lastActiveModule. */
  setActiveModule: (module: ModuleId) => void;
  /** Restore the last active module from persisted storage (called on app launch). */
  restoreLastActiveModule: () => Promise<void>;
}

export type UIStore = UIState & UIActions;

// ─── Helpers ─────────────────────────────────────────────────────────────────

let notificationCounter = 0;

function generateNotificationId(): string {
  notificationCounter += 1;
  return `notif_${Date.now()}_${notificationCounter}`;
}

/** Valid module IDs for runtime validation when restoring from storage. */
const VALID_MODULE_IDS: readonly ModuleId[] = [
  'journal',
  'tasks',
  'calendar',
  'habits',
  'focus',
  'countdown',
  'notes',
];

/**
 * The default module shown on launch. The Journal is intentionally NOT the
 * default — it is hidden and locked, reachable only via its keyboard shortcut.
 */
const DEFAULT_MODULE: ModuleId = 'tasks';

// ─── Store ───────────────────────────────────────────────────────────────────

export const useUIStore = create<UIStore>((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────────────
  currentView: 'calendar',
  sidebarOpen: true,
  notifications: [],
  selectedDate: null,
  activeModule: DEFAULT_MODULE,
  lastActiveModule: DEFAULT_MODULE,

  // ── Actions ────────────────────────────────────────────────────────────────
  setView: (view) => {
    set({ currentView: view });
  },

  toggleSidebar: () => {
    set({ sidebarOpen: !get().sidebarOpen });
  },

  addNotification: (message, type = 'info') => {
    const notification: Notification = {
      id: generateNotificationId(),
      message,
      type,
      createdAt: Date.now(),
    };
    set({ notifications: [...get().notifications, notification] });
  },

  dismissNotification: (id) => {
    set({
      notifications: get().notifications.filter((n) => n.id !== id),
    });
  },

  setSelectedDate: (date) => {
    set({ selectedDate: date });
  },

  setActiveModule: (module) => {
    const previousModule = get().activeModule;
    set({ activeModule: module, lastActiveModule: previousModule });

    // Persist lastActiveModule to IndexedDB appSettings (fire-and-forget)
    db.appSettings.put({ key: 'lastActiveModule', value: module }).catch(() => {
      // Silently ignore persistence errors — app can still function
    });
  },

  restoreLastActiveModule: async () => {
    try {
      const setting = await db.appSettings.get('lastActiveModule');
      if (
        setting &&
        typeof setting.value === 'string' &&
        VALID_MODULE_IDS.includes(setting.value as ModuleId) &&
        // The Journal is hidden/locked — never auto-restore into it on launch.
        setting.value !== 'journal'
      ) {
        const restoredModule = setting.value as ModuleId;
        set({ activeModule: restoredModule, lastActiveModule: restoredModule });
      }
    } catch {
      // If IndexedDB read fails, keep the default module.
    }
  },
}));
